import React, { MouseEventHandler, useLayoutEffect, useState } from "react";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import InPlaceError from "components/in-place-error/InPlaceError";
import "./input.scss";
import classNames from "classnames";
import { GLOBAL_LINK_NO_TX_CLASS } from "utilities/globalLinkNoTx";
import { PHONE_PLACEHOLDER } from "utilities/Constant";

export const Label = ({ label }: { label: string }) => {
  return (
    <div className="input-label__wrapper">
      <label htmlFor="label" className="input-label">
        {label}
      </label>
    </div>
  );
};

export const InputSubText = ({ subText }: { subText: string }) => {
  return <label className="input__subtext">{subText}</label>;
};

const Icon = ({
  icon,
  isIconPointer,
  onIconClick,
}: {
  icon: IconDefinition;
  isIconPointer?: boolean;
  onIconClick?: React.MouseEventHandler<HTMLButtonElement>;
}) => {
  return (
    <button
      type="button"
      className={classNames("input--icon", { "input--icon--pointer": isIconPointer })}
      onClick={onIconClick}
    >
      <FontAwesomeIcon icon={icon} />
    </button>
  );
};

export interface InputProps {
  dataType?: string;
  icon?: IconDefinition;
  label?: string;
  subText?: string;
  placeholder?: string;
  errorMessage?: string;
  type?: string;
  maxLength?: number;
  onIconClick?: MouseEventHandler<HTMLButtonElement>;
  isIconPointer?: boolean;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  inputBorderHighlightForError?: boolean;
  name: string;
  className?: string;
  containerClassName?: boolean;
  onChange?(e: React.FormEvent<HTMLInputElement>): void;
  onBlur?(e: React.FormEvent<HTMLInputElement>): void;
  onFocus?(e: React.FormEvent<HTMLInputElement>): void;
  /** When true (default), marks input as GlobalLinkNoTx for PII fields. Set false to allow Transperfect translation (e.g. login placeholders). */
  excludeFromTranslation?: boolean;
  /** Increment to clear zip format error after programmatic ZIP autofill (e.g. address lookup). */
  zipAutofillResetKey?: number;
}

export const ZIP_NUMBERS_ONLY_ERROR = "Enter numbers only";

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      dataType,
      icon,
      label,
      subText,
      placeholder,
      errorMessage,
      inputBorderHighlightForError,
      type = "text",
      maxLength,
      onIconClick,
      isIconPointer = false,
      isReadOnly = false,
      isDisabled = false,
      name,
      onChange,
      onBlur,
      className,
      containerClassName,
      onFocus,
      excludeFromTranslation = true,
      zipAutofillResetKey,
    }: InputProps,
    ref
  ) => {
    const [zipFormatError, setZipFormatError] = useState<string | undefined>();
    const displayedError = zipFormatError ?? errorMessage;

    useLayoutEffect(() => {
      if (dataType !== "zipCode" || zipAutofillResetKey === undefined || zipAutofillResetKey === 0) {
        return;
      }

      setZipFormatError(undefined);
    }, [dataType, zipAutofillResetKey]);

    const inputClass = classNames(
      {
        input: true,
        "input--read-only": isReadOnly,
        "input--error": displayedError,
        "input-error-border": inputBorderHighlightForError,
      },
      excludeFromTranslation && GLOBAL_LINK_NO_TX_CLASS,
      className
    );

    const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
      if (dataType === "zipCode" && /^\d+$/.test(e.currentTarget.value)) {
        setZipFormatError(undefined);
      }
      onChange?.(e);
    };

    const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
      const inputValue = e.currentTarget.value;

      if (label === "Phone Number") {
        e.currentTarget.value = inputValue.replace(/\D/g, "");

        if (e.currentTarget.value.length > 10) {
          e.currentTarget.value = e.currentTarget.value.slice(0, 10);
        }
      }

      if (dataType === "zipCode") {
        const zipMaxLength = maxLength && maxLength > 0 ? maxLength : 5;
        const digitsOnly = inputValue.replace(/\D/g, "");

        if (inputValue !== digitsOnly) {
          setZipFormatError(ZIP_NUMBERS_ONLY_ERROR);
        } else {
          setZipFormatError(undefined);
        }

        e.currentTarget.value = digitsOnly.slice(0, zipMaxLength);
        return;
      }

      if (maxLength && maxLength > 0) {
        if (inputValue.length > maxLength) {
          e.currentTarget.value = inputValue.slice(0, maxLength);
        }
      }
    };

    const computedPlaceholder = label === "Phone Number" ? PHONE_PLACEHOLDER : placeholder;
    const containerClassNames = classNames(
      {
        "input-container": true,
        "input-container input-city": dataType === "city",
        "input-container input-zipCode": dataType === "zipCode",
        "input-container input-unitNumber": dataType === "addressUnitIdentifier",
        "input-container-custom": containerClassName,
      },
      className
    );

    const getMaxLength = () => {
      switch (dataType) {
        case "phone":
          return 10;
        case "addressUnitIdentifier":
          return 8;
        default:
          return maxLength;
      }
    };

    return (
      <div className={containerClassNames}>
        {label && <Label label={label} />}
        <div className="input__wrapper">
          <input
            name={name}
            ref={ref}
            onChange={handleChange}
            onBlur={onBlur}
            onFocus={onFocus}
            className={inputClass}
            disabled={isDisabled}
            type={type}
            placeholder={computedPlaceholder}
            maxLength={getMaxLength()}
            minLength={label === "Phone Number" ? 10 : undefined}
            inputMode={dataType === "zipCode" ? "numeric" : undefined}
            readOnly={isReadOnly}
            aria-label={label}
            aria-invalid={displayedError ? true : undefined}
            onInput={handleInput}
            data-testid="pii-input"
          />
          {icon && <Icon icon={icon} isIconPointer={isIconPointer} onIconClick={onIconClick} />}
          {displayedError && <InPlaceError errorPhrase={displayedError} />}
          {subText && <InputSubText subText={subText} />}
        </div>
      </div>
    );
  }
);

Input.displayName = "Input";
